/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { EnterpriseLearnerInsights_MetricTimeFrame } from '__generated__/graphql-types';

import { FormattedHTMLMessage } from 'js/lib/coursera.react-intl';
import { useNavigate } from 'js/lib/useRouter';

import { Divider, InlineNotification, Typography2, breakpoints } from '@coursera/cds-core';
import { GradingIcon, StopwatchIcon } from '@coursera/cds-icons';
import { Button, Link } from '@coursera/event-pulse/react';

import LearningInsightsCard, { SubSectionName } from 'bundles/enterprise-admin-home/components/LearningInsightsCard';
import LearningInsightsRow, {
  LearningInsightsRowPlaceholder,
} from 'bundles/enterprise-admin-home/components/LearningInsightsRow';
import { convertMetricTimeFrameToInt } from 'bundles/enterprise-admin-home/utils/LearningInsightsUtils';
import { useEnterpriseAdminTrackingData } from 'bundles/enterprise-admin-hooks/hooks/useEnterpriseAdminTrackingData';
import EnterprisePermissionsRequired from 'bundles/enterprise-admin-roles/components/EnterprisePermissionsRequired';
import { formatNumber } from 'bundles/enterprise-ui/utils';
import { useIntl } from 'bundles/page/lib/useIntl';

import _t from 'i18n!nls/enterprise-admin-home';

const styles = {
  withMarginTop: css`
    margin-top: var(--cds-spacing-200);
  `,
  errorNotification: css`
    margin: var(--cds-spacing-300) 0;
  `,
  learnerActivityDivider: css`
    margin-bottom: var(--cds-spacing-150);
  `,
  footerContainer: css`
    padding-bottom: var(--cds-spacing-200);

    ${breakpoints.only('xs')} {
      padding-bottom: var(--cds-spacing-150);
    }
  `,
};

export type Props = {
  numOfLearnersMakingProgress: number;
  learnersMakingProgressChangePercentage: number;
  numOfLearningHours: number;
  numOfLearningHoursChangePercentage: number;
  hasError?: boolean;
  isLoading?: boolean;
  timeframe: EnterpriseLearnerInsights_MetricTimeFrame;
  orgId: string;
  orgSlug: string;
  numOfEnrollments: number;
  numOfEnrollmentsChangePercentage: number;
};

const LearnerActivityCard = ({
  numOfLearnersMakingProgress,
  learnersMakingProgressChangePercentage,
  numOfLearningHours,
  numOfLearningHoursChangePercentage,
  timeframe,
  hasError,
  isLoading,
  orgId,
  orgSlug,
  numOfEnrollments,
  numOfEnrollmentsChangePercentage,
}: Props) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const enterpriseAdminTrackingData = useEnterpriseAdminTrackingData();

  const renderBody = () => {
    if (hasError) {
      return (
        <InlineNotification
          css={styles.errorNotification}
          severity="error"
          title={_t('There was a problem fetching learner activity data')}
        >
          {_t('Please try refreshing the page or attempting again at a later time.')}
        </InlineNotification>
      );
    } else if (isLoading) {
      return (
        <>
          <LearningInsightsRowPlaceholder />
          <Divider />
          <LearningInsightsRowPlaceholder />
        </>
      );
    } else {
      const activeLearners = (
        <LearningInsightsRow
          icon={GradingIcon}
          metricName={_t('Learners making progress')}
          metricNum={numOfLearnersMakingProgress}
          percentageOfChange={learnersMakingProgressChangePercentage}
          timeframe={timeframe}
          aboutMetricHeader={_t('About learners making progress')}
          aboutMetricBody={
            <Typography2 component="p">
              {_t('The total number of unique learners making progress, contributing to the Total Hours metric.')}
            </Typography2>
          }
          metricMeasurement={intl.formatMessage(
            _t('{numOfLearnersMakingProgress, plural, =0 {learners} one {learner} other {learners}}'),
            { numOfLearnersMakingProgress }
          )}
          compact
        />
      );
      const learningHours = (
        <LearningInsightsRow
          icon={StopwatchIcon}
          metricName={_t('Learning hours')}
          metricNum={numOfLearningHours}
          percentageOfChange={numOfLearningHoursChangePercentage}
          timeframe={timeframe}
          aboutMetricHeader={_t('About learning hours')}
          aboutMetricBody={
            <Typography2 component="p">
              {_t(
                'The total number of hours your learners have spent progressing through courses in the selected time period.'
              )}
            </Typography2>
          }
          metricMeasurement={intl.formatMessage(
            _t('{numOfLearningHours, plural, =0 {hours} one {hour} other {hours}}'),
            { numOfLearningHours }
          )}
          compact
        />
      );

      return (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: var(--cds-spacing-200);
            padding-bottom: var(--cds-spacing-200);
          `}
        >
          <div
            css={css`
              width: 100%;
            `}
          >
            <div
              css={css`
                display: flex;
                flex-direction: column;
                ${breakpoints.up('md')} {
                  flex-direction: row;
                }
              `}
            >
              {activeLearners}
              {learningHours}
            </div>
            <EnterprisePermissionsRequired domain="ORGANIZATION" domainId={orgId} subdomain="ANALYTICS" verb="VIEW">
              <Link
                typographyVariant="bodyPrimary"
                variant="quiet"
                href={`/o/${orgSlug}/admin/analytics/learner-activity?dateRange=${convertMetricTimeFrameToInt(
                  timeframe
                )}`}
                trackingData={{
                  ...enterpriseAdminTrackingData,
                  button: {
                    name: 'enterprise_admin_explore_learner_activity',
                  },
                  pageSection: {
                    sectionName: 'learner_insights',
                    subsectionName: 'learner_activity',
                  },
                }}
                target="_blank"
                rel="noopener noreferrer"
              >
                {_t('Explore learner activity')}
              </Link>
            </EnterprisePermissionsRequired>
          </div>
          <EnterprisePermissionsRequired domain="ANY_PROGRAM" subdomain="CONTENT_ASSIGNMENT" verb="EDIT">
            <Divider css={{ width: '100%' }} />
            <Typography2 component="p">
              <FormattedHTMLMessage
                message={_t(
                  'In the past <strong>{timeframe} days</strong>, your organization had {enrollments} {change}.'
                )}
                timeframe={
                  {
                    THIRTY_DAYS: 30,
                    SIXTY_DAYS: 60,
                    NINETY_DAYS: 90,
                  }[timeframe]
                }
                enrollments={
                  <strong>
                    {intl.formatMessage(
                      _t('{count} {count, plural, =0 {enrollments} one {enrollment} other {enrollments}}'),
                      { count: formatNumber(numOfEnrollments) }
                    )}
                  </strong>
                }
                change={
                  <Typography2
                    component="span"
                    color={numOfEnrollmentsChangePercentage <= 0 ? 'error' : 'success'}
                  >{`(${formatNumber(numOfEnrollmentsChangePercentage, true)}%)`}</Typography2>
                }
              />
              &nbsp;
              {_t("Empower your learners' growth and development by assigning top content across Coursera.")}
            </Typography2>
            <Button
              variant="secondary"
              size="small"
              trackingData={{
                ...enterpriseAdminTrackingData,
                button: {
                  name: 'enterprise_admin_assign_content',
                },
                pageSection: {
                  sectionName: 'learner_insights',
                  subsectionName: 'learner_activity',
                },
              }}
              onClick={() => {
                navigate({ pathname: `/o/${orgSlug}/assign`, search: '?ref=admin-home' });
              }}
              id="assign"
            >
              {_t('Assign content')}
            </Button>
          </EnterprisePermissionsRequired>
        </div>
      );
    }
  };

  return (
    <LearningInsightsCard
      subSectionName={SubSectionName.LearnerActivity}
      header={_t('Learner activity')}
      timeframe={timeframe}
    >
      {renderBody()}
    </LearningInsightsCard>
  );
};

export default LearnerActivityCard;
